/**
 * ONE ACT PER THING AT A TIME — answered synchronously, because a second press does not wait.
 *
 * THE DEFECT THIS EXISTS FOR. The bridge panel's Disconnect and Connect-again are pressed while the
 * row is still on screen, because the row stays there for the whole round trip. Press twice and the
 * server answers the second one `no such channel of yours` — a 404, because the first press worked —
 * and the panel says *"Could not disconnect that channel."* about a channel it just disconnected.
 * That is the harm the panel already guards one step over, where its own comment reads *"never
 * report a failed RE-READ as a failed ACT"*. A duplicate press is the same sentence about the same
 * act, and the rule as written did not reach it.
 *
 * WHY NOT A PIECE OF COMPONENT STATE. Because state is not readable in time. Two presses inside one
 * frame both run against the render that was on screen when the first landed, so both see `working`
 * as null and both call the server; the disabled attribute only arrives with the next render. A gate
 * that decides in the caller's own tick is the only kind that can answer this — which is why `claim`
 * both asks and takes, and why a renderer keeps one of these in a ref rather than in state.
 *
 * WHY IT IS HERE rather than written twice. Two clients have this panel, and *a fix applied to one
 * client is not applied to two* is the oldest recurring note in `COMMUNICATION.md`. Core is where
 * both can see it, and where it is tested once.
 *
 * KEYED, NOT GLOBAL. Disconnecting one channel must leave the others pressable — a leader with a
 * slow request on one row has not stopped using the panel.
 */
export interface OneAtATime {
  /**
   * Take the key if it is free, in this tick. True means the caller owns it and must release it;
   * **false means somebody already has it and the caller should do nothing at all** — not retry,
   * not report, not draw. The act it duplicates is already under way.
   */
  claim(key: string): boolean;
  /** Give it back. Safe to call for a key that is not held, so a `finally` needs no condition. */
  release(key: string): void;
  /** Every key in flight, for a renderer to shut the controls it is drawing. */
  running(): readonly string[];
}

export function oneAtATime(): OneAtATime {
  const held = new Set<string>();
  return {
    claim: (key: string): boolean => {
      if (held.has(key)) return false;
      held.add(key);
      return true;
    },
    release: (key: string): void => { held.delete(key); },
    running: (): readonly string[] => [...held],
  };
}
